import client from "@/api/client"
import {
  authGenericResponseSchema,
  authSessionResponseSchema,
  authUserResponseSchema,
} from "@/api/schemas/auth"
import { usersListResponseSchema as userListSchema } from "@/api/schemas/user"
import type { BaseResponse } from "@/types/api"

export const login = async (payload: { identifier: string; password: string }) => {
  const identifier = payload.identifier.trim()
  const body = {
    identifier,
    email: identifier,
    password: payload.password,
    appType: "BUSINESS",
  }
  const response = await client.post("/api/v1/auth/login", body)
  return authSessionResponseSchema.parse(response.data)
}

export const checkUsername = async (username: string) => {
  const response = await client.get("/api/v1/auth/username/check", {
    params: { username: username.trim() },
  })
  return response.data as {
    data?: { available?: boolean; username?: string }
    message?: string
  }
}

export const register = async (payload: unknown) => {
  const response = await client.post("/api/v1/auth/signup", payload)
  return authSessionResponseSchema.parse(response.data)
}

export const linkBusinessOwnerAccount = async (payload: {
  email: string
  password: string
  phoneNumber?: string
}) => {
  const response = await client.post(
    "/api/v1/auth/link-business-owner-account",
    payload
  )
  return authSessionResponseSchema.parse(response.data)
}

export const forgotPassword = async (payload: unknown) => {
  const response = await client.post("/api/v1/auth/forgotPassword", payload)
  return authGenericResponseSchema.parse(response.data)
}

export const changePassword = async (payload: unknown) => {
  const response = await client.post("/api/v1/auth/changePassword", payload)
  return authGenericResponseSchema.parse(response.data)
}

export const verifyOtp = async (payload: unknown) => {
  const response = await client.post("/api/v1/auth/verifyOtp", payload)
  return authGenericResponseSchema.parse(response.data)
}

export const updateUser = async (payload: unknown) => {
  const response = await client.patch("/api/v1/auth/updateUser", payload)
  return authUserResponseSchema.parse(response.data)
}

export const verifyEmail = async (payload: {
  phoneNumber?: string | null
  email?: string | null
}) => {
  const response = await client.post(
    `/api/v1/auth/sendOtp?phoneNumber=${payload?.phoneNumber ?? ""}&email=${payload?.email ?? ""
    }`,
    payload
  )
  return authGenericResponseSchema.parse(response.data)
}

export const oauthAuthentication = async (
  payload: unknown,
  source: "GOOGLE" | "APPLE"
) => {
  const response = await client.post(
    `/api/v1/auth/google?source=${source}&appType=BUSINESS`,
    payload
  )
  return authSessionResponseSchema.parse(response.data)
}

export const googleAuthentication = async (payload: unknown) =>
  oauthAuthentication(payload, "GOOGLE")

export const appleAuthentication = async (payload: unknown) =>
  oauthAuthentication(payload, "APPLE")
export const getUsers = async (params: Record<string, unknown>) => {
  const response = await client.get("/api/v1/auth/users", { params })
  return userListSchema.parse(response.data)
}

export const getAdmins = async (params: Record<string, unknown>) => {
  const response = await client.get("/api/v1/auth/admins", { params })
  return userListSchema.parse(response.data)
}

export const addAdminProfile = async (payload: {
  email: string
  name: string
  image?: string
  imageRef?: string
}) => {
  const response = await client.post("/api/v1/auth/addAdminProfile", payload)
  return authGenericResponseSchema.parse(response.data)
}

export const importAdminsViaExcel = async (payload: {
  admins: Array<{ name: string; email: string }>
}) => {
  const response = await client.post("/api/v1/auth/import-admins-excel", payload)
  return response.data
}

const normalizeAuthGenericResponse = (data: unknown): BaseResponse => {
  const parsed = authGenericResponseSchema.safeParse(data)
  if (parsed.success) return parsed.data

  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>
    const message =
      typeof record.message === "string"
        ? record.message
        : typeof record.msg === "string"
          ? record.msg
          : undefined
    if (message !== undefined) {
      return { ...record, message }
    }
  }

  return { message: undefined }
}

export const removeAdminProfile = async (payload: { email: string }) => {
  const response = await client.post("/api/v1/auth/removeAdminProfile", payload)
  return normalizeAuthGenericResponse(response.data)
}

export const resendAdminInvite = async (payload: { email: string }) => {
  const response = await client.post("/api/v1/auth/resendAdminInvite", payload)
  return normalizeAuthGenericResponse(response.data)
}
